import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  fetchPendingUploads,
  updateJobStatus,
} from "../store/slices/uploadFileSlice";
import {
  upsertUploadJob,
  markCompleted,
  type RecordingUploadStatus,
} from "../store/slices/recordingUploadSlice";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const deriveOnlineFromDeviceRegistered = (
  payload: Record<string, unknown>,
): boolean => {
  if (typeof payload.online === "boolean") {
    return payload.online;
  }

  if (typeof payload.isOnline === "boolean") {
    return payload.isOnline;
  }

  if (typeof payload.status === "string") {
    const status = payload.status.trim().toLowerCase();
    if (["online", "connected", "active", "registered"].includes(status)) {
      return true;
    }
    if (["offline", "disconnected", "inactive"].includes(status)) {
      return false;
    }
  }

  // Registration generally means the agent is currently connected.
  return true;
};

const SSEManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const appToken = useSelector(
    (state: RootState) => state.appAuthReducer.appToken,
  );
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const retryCountRef = useRef(0);
  // Latest token for delayed reconnects: the setTimeout closure would
  // otherwise capture a stale token from the render it was created in.
  const tokenRef = useRef<string | null>(appToken);
  tokenRef.current = appToken;
  const queryClient = useQueryClient();

  // Debouncing for React Query invalidations
  const invalidationTimersRef = useRef<Map<string, number>>(new Map());

  // Accumulate uploads for batch notifications
  const uploadBatchRef = useRef<
    Map<string, { count: number; timeoutId: number }>
  >(new Map());

  // Track jobIds we've already counted for upload notifications to prevent
  // duplicates (TriggerS3ChunkStitch fires one UPLOADED per pipeline with the
  // same jobId, and HandleBatchStatus fires another one for the whole task).
  const seenUploadedJobIdsRef = useRef<Set<string>>(new Set());

  // Closes the current connection and clears timers. `resetRetries` must be
  // false when called from the error path: resetting the retry counter there
  // collapsed the exponential backoff to a constant 1s reconnect storm.
  const closeConnection = (resetRetries = true) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    // Clear all debounce timers
    invalidationTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    invalidationTimersRef.current.clear();
    // Clear upload batch timers
    uploadBatchRef.current.forEach((batch) => clearTimeout(batch.timeoutId));
    uploadBatchRef.current.clear();
    seenUploadedJobIdsRef.current.clear();
    if (resetRetries) {
      retryCountRef.current = 0; // Reset retry count on intentional close
    }
  };

  // Refreshes alarm states and incidents once a burst of events settles, as
  // one evaluation can open or resolve many incidents at once.
  const refreshAlarms = () => {
    const key = "alarms";
    const existing = invalidationTimersRef.current.get(key);
    if (existing) clearTimeout(existing);
    const timerId = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["alarmStates"] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      invalidationTimersRef.current.delete(key);
    }, 300);
    invalidationTimersRef.current.set(key, timerId);
  };

  const connect = async () => {
    if (eventSourceRef.current) {
      // Already connected, no need to reconnect unless explicitly forced
      return;
    }

    const token = tokenRef.current;
    if (!token) {
      console.error("SSE Manager: Cannot connect, token unavailable.");
      closeConnection();
      return;
    }

    const url = `${API_BASE_URL}/v2/sse-events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      console.debug("SSE Manager: connected to", url);
      // After a reconnect, catch up on alarm changes missed while offline.
      if (retryCountRef.current > 0) refreshAlarms();
      retryCountRef.current = 0; // Reset retry count on successful connection
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    // Generic message handler (for unnamed events or debugging raw messages)
    es.onmessage = (event) => {
      try {
        console.debug("SSE Manager: onmessage raw", {
          type: (event as MessageEvent).type,
          data: (event as MessageEvent).data,
        });
      } catch (e) {
        console.error("SSE Manager: Failed to log raw message:", e);
      }
    };

    es.addEventListener("uploadStatusUpdate", (event) => {
      try {
        const statusUpdate = JSON.parse((event as MessageEvent).data);
        dispatch(updateJobStatus(statusUpdate));
      } catch (e) {
        console.error("SSE Manager: Failed to parse uploadStatusUpdate:", e);
      }
    });

    es.addEventListener("deviceRegistered", (event) => {
      try {
        const registeredDevice = JSON.parse(
          (event as MessageEvent).data,
        ) as Record<string, unknown>;
        const deviceId =
          typeof registeredDevice.deviceId === "string"
            ? registeredDevice.deviceId
            : typeof registeredDevice.id === "string"
              ? registeredDevice.id
              : null;

        if (!deviceId) {
          console.warn(
            "SSE Manager: deviceRegistered event missing device id",
            registeredDevice,
          );
          return;
        }

        const deviceName =
          typeof registeredDevice.deviceName === "string" &&
          registeredDevice.deviceName.trim()
            ? registeredDevice.deviceName.trim()
            : null;

        const online = deriveOnlineFromDeviceRegistered(registeredDevice);

        queryClient.setQueryData<Array<{ id: string; online: boolean }>>(
          ["deviceStatus"],
          (current = []) => {
            const existing = current.find((entry) => entry.id === deviceId);
            if (existing) {
              return current.map((entry) =>
                entry.id === deviceId ? { ...entry, online } : entry,
              );
            }
            return [...current, { id: deviceId, online }];
          },
        );

        queryClient.setQueryData<Array<Record<string, unknown>>>(
          ["devices"],
          (current) => {
            if (!current) return current;
            return current.map((device) =>
              device.id === deviceId ? { ...device, online } : device,
            );
          },
        );

        // Invalidate React Query cache so DeviceManagement refetches the list.
        // The agent_version is now stored in the DB at registration time, so a
        // single refetch is sufficient to display the correct version immediately.
        queryClient.invalidateQueries({ queryKey: ["devices"] });
        // Anything watching for a specific enrollment (the Add Device page)
        // should re-check as soon as the registration lands.
        queryClient.invalidateQueries({ queryKey: ["enrolledDevices"] });
        toast.info("New Device Registered", {
          description: `${deviceName ?? `Device ${deviceId}`} has been registered. Navigate to Devices tab for setup`,
        });

        // Dispatch custom event for tutorial detection
        window.dispatchEvent(
          new CustomEvent("insaion:deviceRegistered", {
            detail: { deviceId, deviceName, online },
          }),
        );
      } catch (e) {
        console.error("SSE Manager: Failed to parse deviceRegistered:", e);
      }
    });

    es.addEventListener("topicConfigured", (event) => {
      try {
        const configured = JSON.parse((event as MessageEvent).data) as {
          orgId: string;
          deviceId: string;
          topicCount: number;
        };

        window.dispatchEvent(
          new CustomEvent("insaion:topicConfigured", {
            detail: {
              deviceId: configured.deviceId,
              topicCount: configured.topicCount,
            },
          }),
        );
      } catch (e) {
        console.error("SSE Manager: Failed to parse topicConfigured:", e);
      }
    });

    es.addEventListener("storageLimitReached", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          orgId: string;
          deviceId: string;
          deviceName?: string;
          message: string;
        };
        console.debug("SSE Manager: received storageLimitReached", data);
        const displayName =
          data.deviceName && data.deviceName.length > 0
            ? data.deviceName
            : data.deviceId;
        toast.error("Storage Limit Reached", {
          description: `Device ${displayName} cannot be monitored: ${data.message}`,
        });
      } catch (e) {
        console.error("SSE Manager: Failed to parse storageLimitReached:", e);
      }
    });

    es.addEventListener("recordingStatusUpdate", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          orgId: string;
          deviceId: string;
          jobId: string;
          recordingId: string;
          status: string;
          progressPercent: number;
          startRange?: string;
          endRange?: string;
          reason?: string; // why a job FAILED
          code?: string; // reason code, e.g. EVICTED_LOCALLY
        };
        console.debug("SSE Manager: received recordingStatusUpdate", data);

        if (data.status === "UPLOADED") {
          dispatch(markCompleted(data.jobId));
        } else {
          // Any other status, or "" for progress only, adds or updates the job.
          const failed =
            data.status === "FAILED" || data.status === "CANCELLED";
          dispatch(
            upsertUploadJob({
              jobId: data.jobId,
              recordingId: data.recordingId || undefined,
              deviceId: data.deviceId,
              sliceRange:
                data.startRange && data.endRange
                  ? `${new Date(data.startRange).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} - ${new Date(data.endRange).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`
                  : undefined,
              status: (data.status || undefined) as
                | RecordingUploadStatus
                | undefined,
              progress: failed ? 0 : data.progressPercent || 0,
              reason: failed ? data.reason : undefined,
              code: failed ? data.code : undefined,
            }),
          );
        }

        // Debounced invalidation to handle rapid events efficiently
        const queryKey = `deviceRecordingsTimeline-${data.deviceId}`;
        const existingTimer = invalidationTimersRef.current.get(queryKey);

        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Invalidate after 500ms of no new events for this device
        const timerId = window.setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ["deviceRecordingsTimeline", data.deviceId],
          });
          queryClient.invalidateQueries({ queryKey: ["fleetRecordings"] });
          invalidationTimersRef.current.delete(queryKey);
        }, 500);

        invalidationTimersRef.current.set(queryKey, timerId);

        // Handle notifications with batching for rapid uploads
        if (data.status === "UPLOADED") {
          // Deduplicate: the same jobId can produce multiple UPLOADED events
          // (one per pipeline from server-side stitching, plus one job-level
          // report from the agent). Only count each job once.
          if (seenUploadedJobIdsRef.current.has(data.jobId)) {
            return;
          }
          seenUploadedJobIdsRef.current.add(data.jobId);

          const batchKey = data.deviceId;
          const existingBatch = uploadBatchRef.current.get(batchKey);

          if (existingBatch) {
            // Accumulate and reset timer
            clearTimeout(existingBatch.timeoutId);
            existingBatch.count += 1;

            const newTimeoutId = window.setTimeout(() => {
              const count = existingBatch.count;
              uploadBatchRef.current.delete(batchKey);

              // Show batched notification
              toast.success(
                count === 1
                  ? "Recording Uploaded"
                  : `${count} Recordings Uploaded`,
                {
                  description:
                    count === 1
                      ? `Recording segment uploaded successfully`
                      : `${count} recording segments uploaded successfully`,
                  duration: 6000, // Longer duration to ensure visibility
                  id: `upload-success-${batchKey}-${Date.now()}`, // Unique ID
                },
              );
            }, 1000); // Wait 1 second after last upload

            existingBatch.timeoutId = newTimeoutId;
          } else {
            // Start new batch
            const timeoutId = window.setTimeout(() => {
              uploadBatchRef.current.delete(batchKey);

              toast.success("Recording Uploaded", {
                description: `Recording segment uploaded successfully`,
                duration: 6000,
                id: `upload-success-${batchKey}-${Date.now()}`,
              });
            }, 1000);

            uploadBatchRef.current.set(batchKey, { count: 1, timeoutId });
          }
        } else if (data.status === "FAILED") {
          // Always show failures immediately with unique ID
          toast.error("Upload Failed", {
            description: data.reason
              ? `Recording ${data.recordingId}: ${data.reason}`
              : `Failed to upload recording ${data.recordingId}`,
            duration: 8000, // Longer for errors
            id: `upload-failed-${data.jobId}`, // Prevent duplicate toasts for same job
          });
        }
      } catch (e) {
        console.error("SSE Manager: Failed to parse recordingStatusUpdate:", e);
      }
    });

    es.addEventListener("alarmNotification", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          orgId: string;
          type: string;
          title: string;
          body: string;
          incidentId: string;
        };
        console.debug("SSE Manager: received alarmNotification", data);
        // The toast comes from the notification center's websocket, which
        // receives the same event; this one only refreshes the alarm pages.
        refreshAlarms();
      } catch (e) {
        console.error("SSE Manager: Failed to parse alarmNotification:", e);
      }
    });

    // An alarm's state or incidents changed: refresh the alarm pages instead
    // of having them poll.
    es.addEventListener("alarmStateChanged", () => refreshAlarms());

    es.onerror = (error) => {
      console.error("SSE Manager: Error:", error);
      closeConnection(false); // Close current connection, keep retry count for backoff
      const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000); // Exponential backoff, max 30s
      retryCountRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };
  };

  useEffect(() => {
    if (appToken) {
      dispatch(fetchPendingUploads());
      // Always keep SSE connected when authenticated so events like deviceRegistered are received
      connect();
    } else {
      // If not authenticated, ensure connection is closed
      closeConnection();
    }

    return () => {
      closeConnection(); // Cleanup on unmount
    };
  }, [appToken]);

  return null;
};

export default SSEManager;
